import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { SWATCH_WIDTH } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/OptionSelection/SpecialOptionPicker";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { IProductVariationMap } from "@faire/web-api--source/indigofair/data/IProductVariationMap";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
var SpecialOptionVariationBase = function(props) {
    var isSSR = useIsSSR();
    var getOption = function() {
        if (props.displayType === IProductVariationMap.IEntry.VariationDisplayType.SWATCH) {
            return /*#__PURE__*/ _jsx(SwatchOptionWrapper, {
                $selected: props.selected,
                $hovered: props.hovered,
                children: /*#__PURE__*/ _jsx(SwatchOption, {
                    "aria-label": props.variationValue,
                    $imageUrl: props.imageUrl,
                    $disabled: disabled,
                    role: "img",
                    $isSSR: isSSR,
                    children: disabled && !props.isHidden ? /*#__PURE__*/ _jsx(DisabledColorLine, {}) : null
                })
            });
        }
        return /*#__PURE__*/ _jsx(ShortOption, {
            $selected: props.selected,
            $hovered: props.hovered,
            children: /*#__PURE__*/ _jsx(OptionTypography, {
                $isDisabled: disabled,
                color: disabled ? "pewter" : undefined,
                children: props.variationValue
            })
        });
    };
    var disabled = function() {
        return props.unavailable || props.outOfStock;
    }();
    return /*#__PURE__*/ _jsx(OptionWrapper, {
        role: "radio",
        "aria-checked": props.selected,
        "aria-label": props.variationValue,
        onClick: props.onVariationClick,
        onMouseEnter: function() {
            return props.setHoveredValue(props.variationValue);
        },
        onMouseLeave: function() {
            return props.setHoveredValue();
        },
        "aria-hidden": props.isHidden,
        children: getOption()
    });
};
export var SpecialOptionVariation = observer(SpecialOptionVariationBase);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Radio/Radio#Radio"
 */ var OptionWrapper = styled.button.withConfig({
    componentId: "sc-df550eed-0"
})([
    "outline:none;padding:2px 0px;"
]);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */ var SwatchOptionWrapper = styled.div.withConfig({
    componentId: "sc-df550eed-1"
})([
    "border:1px solid transparent;",
    ";",
    " display:flex;justify-content:center;align-items:center;padding:3px;border-radius:50%;"
], function(props) {
    return props.$selected ? css([
        "border:1px solid ",
        ";"
    ], Core.border.default) : "";
}, function(props) {
    return props.$hovered ? css([
        "border:1px solid ",
        ";"
    ], Core.border.subdued) : "";
});
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */ var SwatchOption = styled.div.withConfig({
    componentId: "sc-df550eed-2"
})([
    "border-radius:50%;border:1px solid ",
    ";width:40px;height:40px;",
    ";",
    ";background-position:center;background-size:cover;display:flex;align-items:center;justify-content:center;",
    "{position:relative;}"
], Core.border.muted, function(props) {
    return props.$disabled ? "opacity: 0.5" : "";
}, function(props) {
    return props.$imageUrl ? css([
        "background:url(",
        ");"
    ], function() {
        return optimizeImageUrlFillSquare({
            url: props.$imageUrl
        }, props.$isSSR, SWATCH_WIDTH);
    }) : "";
}, responsiveMediaQueries.mobile);
/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Swatch/Swatch#Swatch"
 */ var DisabledColorLine = styled.div.withConfig({
    componentId: "sc-df550eed-3"
})([
    "width:52px;height:4px;transform:rotate(-45deg);position:absolute;z-index:1;background:",
    ";border-top:1px solid ",
    ";border-bottom:1px solid ",
    ";"
], Core.border.default, Core.border.muted, Core.border.muted);
var ShortOption = styled.div.withConfig({
    componentId: "sc-df550eed-4"
})([
    "border:1px solid ",
    ";",
    ";",
    ";min-width:64px;",
    "{min-width:56px;}padding:20px;background-color:",
    ";"
], Core.border.muted, function(props) {
    return props.$hovered ? css([
        "border:1px solid ",
        ";"
    ], Core.border.subdued) : "";
}, function(props) {
    return props.$selected ? css([
        "border:1px solid ",
        ";"
    ], Core.border.default) : "";
}, responsiveMediaQueries.desktopAndAbove, Core.surface.inverse);
var OptionTypography = styled(Typography).withConfig({
    componentId: "sc-df550eed-5"
})([
    "",
    ";"
], function(props) {
    return props.$isDisabled ? "text-decoration:line-through" : "";
});
